import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { DefaultError } from '@tanstack/react-query';
import { Button, DropdownMenu, TableItem } from 'components/atoms';
import LabelNotificationNew from 'components/molecules/labeledStatus/LabelNotificationNew';
import { Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useComponentVisible, useHasPermission } from 'hooks';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useStoreState } from 'providers/StoreStateContext';
import { useTranslation } from 'react-i18next';
import { PiArchive, PiDotsThreeVerticalBold, PiEye } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import {
  useCompleteNotification,
  useGetNotificationListInfinite,
  useGetUnreadNotification,
  usePutNotificationsArchiveAll,
  usePutNotificationsReadAll,
  useVisitNotification,
} from 'services/notifications/index';
import { NOTIFICATION_STATUS } from 'services/notifications/types/enums';
import {
  INotificationComplete,
  INotificationContent,
} from 'services/notifications/types/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import {
  formatNotificationDate,
  formatNotificationTime,
  getNotificationDateSource,
  getNotificationDisplayName,
  getNotificationNavigationPath,
  getNotificationReceivedTypeLabel,
} from 'utils/notifications/notification.utils';
import { onErrorShowToast } from 'utils/service/errorHandler';
import { getNodes } from 'utils/table/nodes.utils';

enum SORT_COLUMNS {
  notification,
  date,
  status,
}

const Notifications = () => {
  usePermissionGuard('NotificationPermissionsLIST**');
  const hasPermission = useHasPermission();

  const { t } = useTranslation();
  const i18nextPrefix = 'notification';
  const navigate = useNavigate();

  const columnsVisibility = useTableColumnsVisibility(
    'NotificationsReceived',
    SORT_COLUMNS
  );
  const { tokenData } = useStoreState();

  const unreadNotificationCount = useGetUnreadNotification();
  const readAllNotification = usePutNotificationsReadAll();
  const archiveAllNotification = usePutNotificationsArchiveAll();

  const { data, isLoading, refetch, fetchNextPage, isFetchingNextPage } =
    useGetNotificationListInfinite({});

  const { mutate: completeNotification } = useCompleteNotification();
  const { mutate: visitNotification } = useVisitNotification();

  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false);

  const tableData = getNodes<INotificationContent>(data?.pages.flat());

  const hasDetailPermission = hasPermission('NotificationPermissionsLIST**', 16);

  const handleCompletedClick = async (item: INotificationContent) => {
    const { id } = item;

    if (tokenData?.UserId && id) {
      const notificationData: INotificationComplete = {
        notificationId: id,
        userId: tokenData?.UserId,
      };

      await completeNotification(notificationData, {
        onError: (error: DefaultError) => {
          onErrorShowToast(error);
        },
        onSuccess: () => {
          refetch();
        },
      });
    }
  };

  const handleVisitClick = async (item: INotificationContent) => {
    const { id } = item;

    if (tokenData?.UserId && id) {
      const notificationData: INotificationComplete = {
        notificationId: id,
        userId: tokenData?.UserId,
      };

      await visitNotification(notificationData, {
        onError: (error: DefaultError) => {
          onErrorShowToast(error);
        },
        onSuccess: () => {
          refetch();
          unreadNotificationCount.refetch();
        },
      });
    }
  };

  const handleNavigateClick = (item: INotificationContent): void => {
    const path = getNotificationNavigationPath(item, hasPermission);

    if (!path) {
      return;
    }

    navigate(path);

    if (item.notificationStatus === NOTIFICATION_STATUS.NotDisplayed) {
      handleVisitClick(item);
    }
  };

  const onSelectChange = (action: Action, state: State) => {
    if (!hasDetailPermission) {
      return;
    }

    const selectedItem: INotificationContent | undefined = tableData.nodes.find(
      ({ id }) => id === state.id
    );

    if (selectedItem) {
      handleNavigateClick(selectedItem);
    }
  };

  const select = useRowSelect(tableData, {
    onChange: onSelectChange,
  });

  const tableSelect = hasDetailPermission ? select : undefined;

  const readAllNotificationFunction = async () => {
    await readAllNotification.mutateAsync(undefined, {
      onSuccess: async () => {
        await refetch();
        unreadNotificationCount.refetch();
      },
    });
  };

  const archiveAllNotificationFunction = async () => {
    await archiveAllNotification.mutateAsync(undefined, {
      onSuccess: async () => await refetch(),
    });
  };

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t(`${i18nextPrefix}.title.list`)}
          hideBackButton
          topbarContent={
            <>
              <Button
                isClear
                variant={BUTTON_VARIANT.default}
                onClick={() => navigate('/notifications/archive/')}>
                <Button.Text text={t(`notification-archive.title.list`)} />
                <Button.Icon icon={<PiArchive />} />
              </Button>
              <Table.CustomColumnsButton
                columns={SORT_COLUMNS}
                isClear
                visibility={columnsVisibility}
                i18nextPrefix='notification.table'
                customI18next=''
              />
              <Button
                onlyIcon
                isClear
                onClick={handleComponentVisible}
                outsideChildren={
                  <DropdownMenu
                    ref={ref}
                    visible={isComponentVisible}
                    style={{ padding: 5 }}
                    justifyPosition={'end'}>
                    <div className='flex flex-col w-full'>
                      <Button
                        variant={BUTTON_VARIANT.default}
                        onClick={() => readAllNotificationFunction()}
                        isClear>
                        <Button.Icon icon={<PiEye />} />
                        <Button.Text
                          text={t(`notification-archive.title.readAll`)}
                        />
                      </Button>

                      <Button
                        variant={BUTTON_VARIANT.default}
                        onClick={() => archiveAllNotificationFunction()}
                        isClear>
                        <Button.Icon icon={<PiArchive />} />
                        <Button.Text
                          text={t(`notification-archive.title.archiveAll`)}
                        />
                      </Button>
                    </div>
                  </DropdownMenu>
                }>
                <Button.Description description={t('button.more-options')} />
                <Button.Icon icon={<PiDotsThreeVerticalBold />} />
              </Button>
            </>
          }
        />
      }>
      <Table
        id={columnsVisibility.tableId}
        select={tableSelect}
        loading={isLoading || isFetchingNextPage}
        onFullyScroll={fetchNextPage}
        data={tableData}
        columns={[
          {
            label: t('notification.table.notification'),
            renderCell: (item: INotificationContent) => (
              <TableItem className='gap-2'>
                <TableItem.Text text={getNotificationDisplayName(item)} />
                <TableItem.Text
                  isSupplementary
                  text={getNotificationReceivedTypeLabel(
                    t,
                    item.notificationType
                  )}
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.notification),
          },
          {
            label: t('notification.table.date'),
            renderCell: (item: INotificationContent) => {
              const dateSource = getNotificationDateSource(item);

              return (
                <TableItem className='gap-2'>
                  <TableItem.Text text={formatNotificationTime(dateSource)} />
                  <TableItem.Text
                    isSupplementary
                    text={formatNotificationDate(dateSource)}
                  />
                </TableItem>
              );
            },
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.date),
          },
          {
            label: '',
            renderCell: (item: INotificationContent) => (
              <TableItem>
                <LabelNotificationNew status={item.notificationStatus} />
              </TableItem>
            ),
            hide: columnsVisibility.hide(SORT_COLUMNS.status),
          },
          {
            label: '',
            renderCell: (item: INotificationContent) => (
              <TableItem justifyPosition='end'>
                <div className='flex gap-2'>
                  {item.notificationStatus ===
                    NOTIFICATION_STATUS.NotDisplayed && (
                    <Button
                      isClear
                      onlyIcon
                      onClick={() => handleVisitClick(item)}>
                      <Button.Description description={t('button.open')} />
                      <Button.Icon icon={<PiEye />} />
                    </Button>
                  )}
                  <Button onlyIcon onClick={() => handleCompletedClick(item)}>
                    <Button.Description description={t('button.archive')} />
                    <Button.Icon icon={<PiArchive />} />
                  </Button>
                </div>
              </TableItem>
            ),
          },
        ]}
        displayMode
      />
    </TopbarPage>
  );
};

export default Notifications;
